import { ArrowLeftIcon } from "@/components/icons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { PERMISSIONS } from "../lib/permissions.ts";
import { errorMessage } from "../lib/query.ts";

import {
  deleteMemoryNote,
  memoryQuery,
  readMemoryNote,
  saveMemoryNote,
  type MemoryEntry,
  type MemoryNote,
} from "../chat/memory.ts";
import { useSession } from "./session.tsx";

/**
 * Lo que el agente recuerda, abierto a quien lo protagoniza (seccion 11).
 *
 * Se carga igual que lo ve el agente: primero el índice -- una línea por nota
 * -- y el contenido solo al abrir una. Es la misma disciplina, y de paso hace
 * evidente por qué el índice tiene que ser corto.
 *
 * En embajadores, encima de las notas iban las mecánicas: una memoria que se
 * versiona en vez de sobrescribirse (§7.3.5). Si las mecánicas de
 * nutriexpertos acaban siendo también documentos, van aquí arriba, separadas
 * de las notas y con su propio scroll.
 */
export function MemoryPanel() {
  const queryClient = useQueryClient();
  const { data: index, isError } = useQuery(memoryQuery());
  const [open, setOpen] = useState<MemoryNote | undefined>();

  /**
   * Abrir una nota trae su contenido, que el índice no lleva.
   *
   * Es una mutación y no una consulta porque la dispara un clic y su resultado
   * no se cachea por sí solo: lo que se guarda es la nota abierta, y cerrarla
   * la descarta.
   */
  const opening = useMutation({
    mutationFn: readMemoryNote,
    onSuccess: setOpen,
  });

  const close = (changed: boolean) => {
    setOpen(undefined);
    if (changed) void queryClient.invalidateQueries({ queryKey: ["memory"] });
  };

  if (open !== undefined) {
    return (
      // El editor se queda con la columna entera y scrollea él.
      <NoteEditor
        note={open}
        onClose={() => {
          close(false);
        }}
        onSaved={() => {
          close(true);
        }}
        onDeleted={() => {
          close(true);
        }}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <section className="flex min-h-0 flex-1 flex-col gap-2">
        <h3 className={`shrink-0 ${SECTION_TITLE}`}>Notas</h3>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {(isError || opening.isError) && (
            <p className="text-sm text-muted-foreground">
              No se pudo cargar la memoria.
            </p>
          )}

          {index?.profile != null && (
            <section className="mb-2 rounded-md border bg-secondary/40 p-3">
              <h4 className={`mb-1 ${SECTION_TITLE}`}>Tu perfil</h4>
              <p className="text-sm">{index.profile}</p>
            </section>
          )}

          {index !== undefined && index.entries.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Todavía no hay nada guardado. El asistente irá anotando cómo
              trabajas a medida que trabajen juntos.
            </p>
          )}

          <ul className="grid gap-1">
            {(index?.entries ?? []).map((entry) => (
              <li key={`${entry.scopeType}:${entry.slug}`}>
                <button
                  type="button"
                  className="grid w-full gap-0.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-secondary"
                  onClick={() => {
                    opening.mutate(entry.slug);
                  }}
                >
                  <span className="flex items-center gap-1.5 font-mono text-sm">
                    {entry.slug}
                    <ScopeTag scope={entry.scopeType} />
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {entry.summary}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {index !== undefined && index.omitted > 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              Hay {index.omitted} notas más que no caben en el índice que ve el
              asistente; las encuentra buscando.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * La etiqueta de alcance. Lo del usuario no la lleva: es lo normal, y marcarlo
 * todo no distingue nada.
 *
 * Las etiquetas van en una tabla y no en un ternario porque así un alcance
 * nuevo en el contrato deja de compilar aquí en vez de dibujarse con el
 * nombre del otro. Antes esto decía "programa" para todo lo que no fuera
 * `client`, y "programa" ni siquiera es un alcance que pueda llegar.
 */
const SCOPE_LABELS = { client: "cliente" } satisfies Record<
  Exclude<MemoryEntry["scopeType"], "user">,
  string
>;

/** Las dos memorias se anuncian igual: si no, no se leen como hermanas. */
const SECTION_TITLE =
  "text-xs font-semibold tracking-wide text-muted-foreground uppercase";

function ScopeTag({ scope }: { scope: MemoryEntry["scopeType"] }) {
  if (scope === "user") return null;
  return (
    <Badge variant="outline" className="text-[0.65rem]">
      {SCOPE_LABELS[scope]}
    </Badge>
  );
}

interface EditorProps {
  readonly note: MemoryNote;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

function NoteEditor({ note, onClose, onSaved, onDeleted }: EditorProps) {
  const [summary, setSummary] = useState(note.summary);
  const [content, setContent] = useState(note.content);

  const dirty = summary !== note.summary || content !== note.content;
  /* Sin `memory.write` la nota se lee: los campos quedan de solo lectura y
     no hay botones, en vez de dejar editar algo que no se va a poder guardar. */
  const canWrite = useSession().can(PERMISSIONS.memoryWrite);

  const save = useMutation({
    mutationFn: () =>
      saveMemoryNote(note.slug, { summary, content, scope: note.scopeType }),
    onSuccess: onSaved,
  });

  const remove = useMutation({
    mutationFn: () => deleteMemoryNote(note.slug, note.scopeType),
    onSuccess: onDeleted,
  });

  const saving = save.isPending || remove.isPending;
  const error = save.error ?? remove.error;

  return (
    <div className="grid min-h-0 flex-1 content-start gap-3 overflow-y-auto">
      <Button variant="ghost" size="sm" className="w-fit" onClick={onClose}>
        <ArrowLeftIcon /> Volver
      </Button>

      <p className="flex items-center gap-1.5 font-mono text-sm">
        {note.slug}
        <ScopeTag scope={note.scopeType} />
      </p>

      <div className="grid gap-1.5">
        <Label htmlFor="note-summary">
          Resumen (es lo único que ve en el índice)
        </Label>
        <Input
          id="note-summary"
          value={summary}
          readOnly={!canWrite}
          maxLength={200}
          onChange={(event) => {
            setSummary(event.target.value);
          }}
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="note-content">Contenido</Label>
        <Textarea
          id="note-content"
          value={content}
          readOnly={!canWrite}
          rows={12}
          maxLength={8000}
          onChange={(event) => {
            setContent(event.target.value);
          }}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {note.origin === "manual"
          ? "Editada a mano."
          : note.origin === "explicit"
            ? "La guardó el asistente porque se lo pediste."
            : "La dedujo el asistente de una conversación."}
      </p>

      {error != null && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      )}

      {canWrite && (
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={!dirty || saving}
            onClick={() => {
              save.mutate();
            }}
          >
            Guardar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={saving}
            onClick={() => {
              remove.mutate();
            }}
          >
            Olvidar
          </Button>
        </div>
      )}
    </div>
  );
}
